import React, { useState, useEffect, useMemo, useCallback } from "react";
import { keyframes } from "@emotion/react";
import {
  Box,
  Card,
  Grid,
  Typography,
  Collapse,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Avatar,
  Chip,
  IconButton,
  Select,
  MenuItem,
  Tooltip,
  TextField,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  EvStation,
  Bolt,
  PlayArrow,
  Stop,
  Power,
  AccessTime,
  Person,
  ErrorOutline,
  Replay,
  KeyboardArrowUp,
  KeyboardArrowDown,
  FiberManualRecord,
  CalendarMonth,
  RestartAlt,
  Tune,
  EuroSymbol,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import DeviceGraph from "../cards/DeviceGraph";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  Tooltip as RechartsTooltip, CartesianGrid,
} from "recharts";
import fetchData from "../../API/Fetch";
import { postData } from "../../API/Post";
import { putData } from "../../API/Put";

/**
 * PlugyChargingStations — section « Bornes de recharge » de Résidence Usage.
 * Détecte les bornes Plugy (parent brand 88, canaux 89..96 = points de charge).
 * Affiche, par borne : en-tête + métriques + tuiles par point (live via
 * /irve/state) + UN panneau de détail sous la grille pour le point sélectionné
 * (« Voir détail »). Rend null si aucune borne.
 */
const PLUGY_PARENT_BRAND = 88;
const PLUGY_CHANNEL_MIN = 89;
const PLUGY_CHANNEL_MAX = 96;
const STATE_POLL_MS = 10000;

// Couleurs de statut alignées sur la palette EcoludiK (teal primaire + rouge maison).
const C_CHARGE = { main: "#26A69A", dark: "#00897B" };  // teal primaire EcoludiK
const C_FREE = { main: "#7cb342", dark: "#689f38" };    // vert clair (libre, distinct du teal)
const C_WAIT = { main: "#ffb300", dark: "#ff8f00" };    // ambre : branché, en attente de tour (cyclage)
const C_FAULT = { main: "#fe3e26", dark: "#e0341e" };   // rouge maison
const C_STOP = { main: "#fe3e26", hover: "#d8341f" };   // bouton Arrêter (rouge)
const C_START = { main: "#34c759", hover: "#2aa84a" };  // bouton Démarrer (vert, on/off)

const pulse = keyframes`
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1); }
`;

// Couleurs structurelles = variables du thème EcoludiK -> harmonisé avec le site
// ET adaptatif light/dark automatiquement (plus de hex en dur qui cassent le clair).
// La section reste transparente : elle se fond dans le dégradé vert de la page.
const BORNE_BG = "var(--bg-main)";       // conteneur borne (panneau maison, subtil)
const CARD_BG = "var(--bg-card)";        // cartes métriques = fond de carte maison (#303E32 en dark)
const TILE_BG = "var(--bg-card)";        // 4 cartes points : même surface thème que les cartes (suit light/dark)
const CARD_BORDER = "var(--border-light)";

// Fraîcheur du dernier uplink au-delà de laquelle la borne est signalée
// « silencieuse » : elle n'émet que sur événement (statut/session/télémétrie),
// donc un silence de quelques heures est normal — au-delà d'un jour, on alerte.
const ONLINE_STALE_H = 24;

// Motifs d'arrêt carte — enum OCPP cs__Reason complète (source RPI Temlab).
// 1/3/7 validés au banc, 4 vu en réel (essais 2026-07-22).
const STOP_REASON_LABELS = {
  0: "Arrêt d'urgence",
  1: "Véhicule débranché",
  2: "Reset matériel",
  3: "Arrêt local",
  4: "Autre",
  5: "Coupure secteur",
  6: "Redémarrage",
  7: "Arrêt à distance",
  8: "Reset logiciel",
  9: "Déverrouillage",
  10: "Autorisation retirée",
};

const ORIGIN_LABELS = { badge: "Badge", local: "Local", screen: "Écran", qr: "QR code", dashboard: "Dashboard" };

// Micro-session « échec de démarrage » (cf firmware/docs/OBSERVABILITE_PROD.md) :
// motif 1 (véhicule débranché) + ~0 Wh + durée < 60 s (ou inconnue). À terme
// exclue de la facturation ; en attendant, on la grise dans l'historique.
function isFailedStart(s) {
  return (
    s.stop_reason === 1 &&
    (s.energy_wh == null || s.energy_wh <= 50) &&
    (s.duration_s == null || s.duration_s < 60)
  );
}

function channelNumber(devEui) {
  const m = /_CH(\d+)$/.exec(devEui || "");
  return m ? parseInt(m[1], 10) : null;
}

function findHistory(deviceHistories, devEui) {
  if (!deviceHistories) return [];
  for (const typeId of Object.keys(deviceHistories)) {
    const byDev = deviceHistories[typeId];
    if (byDev && byDev[devEui]) return byDev[devEui];
  }
  return [];
}

function elapsedHours(startedAt) {
  if (!startedAt) return null;
  const t = new Date(startedAt).getTime();
  if (!t) return null;
  return Math.max(0, (Date.now() - t) / 3600000);
}

function fmtElapsed(startedAt) {
  const h = elapsedHours(startedAt);
  if (h == null) return null;
  const s = Math.floor(h * 3600);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  return hh > 0 ? `${hh}h${String(mm).padStart(2, "0")}` : `${mm} min`;
}

function fmtDuration(seconds) {
  if (seconds == null) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m} min`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// Ancienneté du dernier uplink de la borne, en heures (null si inconnue).
function uplinkAgeHours(timeUpdated) {
  if (!timeUpdated) return null;
  const t = new Date(timeUpdated).getTime();
  if (!t) return null;
  return Math.max(0, (Date.now() - t) / 3600000);
}

function fmtAge(hours) {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 48) return `${Math.round(hours)} h`;
  return `${Math.round(hours / 24)} j`;
}

function titleCase(eui) {
  if (!eui) return eui;
  return eui.charAt(0).toUpperCase() + eui.slice(1);
}

// Montants : toujours deux décimales, virgule française (0,40 € — jamais 0,4 €).
function fmtEuro(euros) {
  return (Math.round(euros * 100) / 100).toFixed(2).replace(".", ",");
}
function fmtCents(cents) {
  return fmtEuro((cents || 0) / 100) + " €";
}

// Estimation du NET après frais bancaires. Frais réels connus de Stripe seul ;
// barème cartes UE standard : 1,5 % + 0,25 € par transaction payante.
// FRAIS_GESTION_PCT = commission de gestion Plugy, non définie à ce jour (0).
const STRIPE_PCT = 0.015;
const STRIPE_FIXED_CENTS = 25;
const FRAIS_GESTION_PCT = 0;
function netCents(grossCents, txCount) {
  if (!grossCents) return 0;
  return Math.max(0, Math.round(grossCents * (1 - STRIPE_PCT - FRAIS_GESTION_PCT))
    - (txCount || 0) * STRIPE_FIXED_CENTS);
}

export default function PlugyChargingStations({ devicesByType, deviceHistories, groupedBy }) {
  const bornes = useMemo(() => {
    const all = [];
    Object.values(devicesByType || {}).forEach((list) => (list || []).forEach((d) => all.push(d)));
    const parents = all.filter((d) => d.device_brand_id === PLUGY_PARENT_BRAND);
    return parents.map((parent) => ({
      parent,
      channels: all
        .filter(
          (d) =>
            d.device_brand_id >= PLUGY_CHANNEL_MIN &&
            d.device_brand_id <= PLUGY_CHANNEL_MAX &&
            (d.dev_eui || "").startsWith(parent.dev_eui + "_CH")
        )
        .sort((a, b) => (channelNumber(a.dev_eui) || 0) - (channelNumber(b.dev_eui) || 0)),
    }));
  }, [devicesByType]);

  if (!bornes.length) return null;

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
        <Avatar sx={{ bgcolor: "#ffa726", width: 34, height: 34 }}>
          <EvStation sx={{ color: "#fff", fontSize: 22 }} />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Bornes de recharge</Typography>
      </Stack>
      {bornes.map((borne) => (
        <Borne key={borne.parent.dev_eui} borne={borne} deviceHistories={deviceHistories} groupedBy={groupedBy} />
      ))}
    </Box>
  );
}

/** Volet « Réglages » du gérant (spec Yan 2026-08-25) — code d'établissement,
 * tarif kWh (future facturation au réel), anti-ventouse et trêve nocturne.
 * Stockés PAR ÉTABLISSEMENT côté backend (PUT /irve/{eui}/settings) ; l'appli
 * Plugy les lit avec un cache ~10 min. */
function BorneSettings({ devEui, open }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [estName, setEstName] = useState("");
  // Représentation « humaine » : € (l'API parle en centimes)
  const [codeOn, setCodeOn] = useState(false);
  const [code, setCode] = useState("");
  const [tarif, setTarif] = useState("");        // €/kWh, "" = non défini
  const [grace, setGrace] = useState(60);        // minutes
  const [idleFee, setIdleFee] = useState("");    // €/min (l'API parle en centimes/min)
  const [nightStart, setNightStart] = useState(22);
  const [nightEnd, setNightEnd] = useState(8);

  useEffect(() => {
    if (!open) return;
    let dead = false;
    (async () => {
      setLoading(true); setErr(null);
      const res = await fetchData(`/irve/${devEui}/settings`);
      if (dead) return;
      if (res && res.establishment_id != null) {
        setEstName(res.establishment_name || "");
        setCodeOn(!!res.access_code);
        setCode(res.access_code || "");
        // Tarifs affichés avec DEUX décimales (0,40 — jamais 0,4)
        setTarif(res.tarif_kwh_cents == null ? "" : fmtEuro(res.tarif_kwh_cents / 100));
        setGrace(res.grace_minutes == null ? 60 : res.grace_minutes);
        setIdleFee(res.idle_fee_cents_min ? fmtEuro(res.idle_fee_cents_min / 100) : "");
        setNightStart(res.night_start_hour == null ? 22 : res.night_start_hour);
        setNightEnd(res.night_end_hour == null ? 8 : res.night_end_hour);
      } else {
        setErr("Réglages indisponibles — la borne est-elle rattachée à une résidence/établissement ?");
      }
      setLoading(false);
    })();
    return () => { dead = true; };
  }, [devEui, open]);

  const toCents = (v) => Math.round((parseFloat(String(v).replace(",", ".")) || 0) * 100);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // « Enregistrer » ouvre d'abord un récapitulatif à confirmer (garde-fou
  // contre les fausses manips : ces réglages pilotent la facturation réelle).
  const askConfirm = () => {
    const trimmed = code.trim();
    if (codeOn && !trimmed) { setErr("Saisissez un code, ou désactivez-le."); return; }
    setErr(null);
    setConfirmOpen(true);
  };

  const save = async () => {
    setConfirmOpen(false);
    const trimmed = code.trim();
    setSaving(true); setMsg(null); setErr(null);
    const body = {
      access_code: codeOn ? trimmed : "",
      grace_minutes: Math.max(0, Number(grace) || 0),
      // Saisie en €/min, l'API parle en centimes/min
      idle_fee_cents_min: idleFee === "" ? 0 : toCents(idleFee),
      night_start_hour: Number(nightStart),
      night_end_hour: Number(nightEnd),
    };
    if (tarif !== "") body.tarif_kwh_cents = toCents(tarif);
    const res = await putData(`/irve/${devEui}/settings`, body);
    const status = res?.status || res?.response?.status;
    if (status && status < 400) {
      setMsg("Réglages enregistrés — l'appli client les applique sous ~10 minutes.");
      // Renormalise l'affichage à deux décimales (« 0,4 » ressaisi -> « 0,40 »)
      if (tarif !== "") setTarif(fmtEuro(toCents(tarif) / 100));
      setIdleFee(toCents(idleFee) > 0 ? fmtEuro(toCents(idleFee) / 100) : "");
    } else {
      setErr(res?.response?.data?.detail || "Échec de l'enregistrement — réessayez.");
    }
    setSaving(false);
    setTimeout(() => setMsg(null), 10000);
  };

  const hourItems = Array.from({ length: 24 }, (_, h) => (
    <MenuItem key={h} value={h}>{h} h</MenuItem>
  ));

  return (
    <Box sx={{ px: { xs: 2, md: 2.5 }, pb: 2 }}>
      <Box sx={{ p: 2, borderRadius: 2, border: "1px dashed", borderColor: CARD_BORDER }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Réglages de l'établissement{estName ? ` — ${estName}` : ""}
        </Typography>
        {loading ? <CircularProgress size={20} /> : (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={<Switch checked={codeOn} onChange={(e) => setCodeOn(e.target.checked)} />}
                label="Code d'établissement requis"
              />
              {codeOn && (
                <TextField
                  size="small" fullWidth label="Code (affiché à l'accueil)"
                  value={code} onChange={(e) => setCode(e.target.value)}
                  inputProps={{ maxLength: 32 }}
                />
              )}
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                size="small" fullWidth label="Tarif (€/kWh)" value={tarif}
                onChange={(e) => setTarif(e.target.value)}
                placeholder="ex : 0,40"
                helperText="Facturation au réel (à venir)"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                size="small" fullWidth type="number" label="Tolérance fin de charge (min)"
                value={grace} onChange={(e) => setGrace(e.target.value)}
                inputProps={{ min: 0, max: 1440 }}
                helperText="Délai pour débrancher"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                size="small" fullWidth label="Tarif ventouse (€/min)" value={idleFee}
                onChange={(e) => setIdleFee(e.target.value)}
                placeholder="0 = désactivé"
                helperText={(() => {
                  const v = parseFloat(String(idleFee).replace(",", "."));
                  return v > 0
                    ? `= ${(v * 60).toFixed(2).replace(".", ",")} €/h`
                    : "Au-delà de la tolérance";
                })()}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Stack direction="row" gap={1} alignItems="center">
                <Select size="small" value={nightStart} onChange={(e) => setNightStart(e.target.value)}>
                  {hourItems}
                </Select>
                <Typography variant="body2">→</Typography>
                <Select size="small" value={nightEnd} onChange={(e) => setNightEnd(e.target.value)}>
                  {hourItems}
                </Select>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Trêve nocturne (ventouse non facturée)
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" gap={2} alignItems="center">
                <Button
                  variant="contained" size="small" disabled={saving}
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                  onClick={askConfirm} sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Enregistrer
                </Button>
                {msg && <Typography variant="caption" color="success.main">{msg}</Typography>}
                {err && <Typography variant="caption" color="error">{err}</Typography>}
              </Stack>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Récapitulatif avant application (les clients paient ces valeurs) */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Confirmer les réglages ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Ces valeurs seront appliquées aux prochains clients sous ~10 minutes.
          </Typography>
          <Stack gap={0.75}>
            {[
              ["Tarif de l'énergie", tarif !== "" ? `${fmtEuro(toCents(tarif) / 100)} €/kWh` : "non défini"],
              ["Tolérance fin de charge", `${Math.max(0, Number(grace) || 0)} min`],
              ["Tarif ventouse", toCents(idleFee) > 0
                ? `${fmtEuro(toCents(idleFee) / 100)} €/min (= ${fmtEuro(toCents(idleFee) * 60 / 100)} €/h)`
                : "désactivé"],
              ["Trêve nocturne", `${nightStart} h → ${nightEnd} h`],
              ["Code d'établissement", codeOn ? `« ${code.trim()} »` : "désactivé"],
            ].map(([label, val]) => (
              <Stack key={label} direction="row" justifyContent="space-between" gap={2}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: "right" }}>{val}</Typography>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} color="inherit"
            sx={{ textTransform: "none" }}>Annuler</Button>
          <Button onClick={save} variant="contained"
            sx={{ textTransform: "none", fontWeight: 700 }}>Confirmer et enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Borne({ borne, deviceHistories, groupedBy }) {
  const { parent, channels } = borne;
  const [liveState, setLiveState] = useState({});
  // Reset carte à distance (0x51) — ADMIN uniquement (récupération d'une
  // carte en mode dégradé sans power-cycle sur site). Le backend refuse si
  // une charge est en cours (409) et le firmware porte la même garde.
  const userDB = useSelector((state) => state.userDB);
  const isAdmin = !!(userDB && userDB.admin);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);
  // Détail de session ouvert PAR DÉFAUT (sur le 1er point) ; reste réductible
  // via « Masquer » / « Réduire le détail », et re-sélectionnable sur une autre tuile.
  const [selected, setSelected] = useState(() => channels.length ? channelNumber(channels[0].dev_eui) : null);
  const [collapsed, setCollapsed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // KPI financier : facturé (appli Plugy) agrégé par jour côté backend.
  const [billing, setBilling] = useState(null);   // [{date, gross_cents, tx}]
  const [finOpen, setFinOpen] = useState(false);

  useEffect(() => {
    let dead = false;
    (async () => {
      const res = await fetchData(`/irve/${parent.dev_eui}/billing/daily?days=14`);
      if (!dead) setBilling(res && res.days ? res.days : []);
    })();
    return () => { dead = true; };
  }, [parent.dev_eui]);

  // Date locale YYYY-MM-DD (le backend agrège en Europe/Paris)
  const todayIso = new Date().toLocaleDateString("sv-SE");
  const finToday = (billing || []).find((d) => d.date === todayIso);
  const finTotal = (billing || []).reduce((a, d) => a + d.gross_cents, 0);
  const finTxTotal = (billing || []).reduce((a, d) => a + d.tx, 0);

  const pollState = useCallback(async () => {
    const res = await fetchData(`/irve/${parent.dev_eui}/state`);
    if (res && res.connectors) setLiveState(res.connectors);
  }, [parent.dev_eui]);

  useEffect(() => {
    pollState();
    const id = setInterval(pollState, STATE_POLL_MS);
    return () => clearInterval(id);
  }, [pollState]);

  const totals = useMemo(() => {
    // Même règle que le graphe cumulé du détail (dédoublonnage horodatage) :
    // le KPI Énergie = somme des cumuls finaux des points, au chiffre près.
    let energy = 0;
    channels.forEach((ch) => {
      const seen = new Set();
      (findHistory(deviceHistories, ch.dev_eui) || []).forEach((e) => {
        if (!e || e.time_created == null || seen.has(e.time_created)) return;
        seen.add(e.time_created);
        const v = Number(e.total_used);
        if (isFinite(v)) energy += v;
      });
    });
    let power = 0, occupied = 0;
    Object.values(liveState).forEach((s) => {
      // « occupé » = session ouverte (branché), y compris en attente de tour
      // (cyclage) et point OUVERT par 0x57 pas encore branché (activation QR
      // ou Démarrer) — en dispo-pilot, état 0 = attribué, jamais libre.
      if (s && (s.occupied || s.charging || s.state_code === 0)) occupied++;
      if (s && typeof s.power_kw === "number") power += s.power_kw;
    });
    return { energy, power, occupied, total: channels.length };
  }, [channels, deviceHistories, liveState]);

  const selChannel = channels.find((ch) => channelNumber(ch.dev_eui) === selected);
  const selIndex = channels.findIndex((ch) => channelNumber(ch.dev_eui) === selected);
  const caretPct = selIndex >= 0 && channels.length ? ((selIndex + 0.5) / channels.length) * 100 : 50;

  return (
    <Card
      elevation={0}
      sx={{
        mb: 3, borderRadius: 3, border: "1px solid", borderColor: CARD_BORDER,
        overflow: "hidden", bgcolor: BORNE_BG,
      }}
    >
      {/* En-tête de borne */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{ width: 4, height: 38, borderRadius: 2, bgcolor: C_CHARGE.main }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {titleCase(parent.name || parent.dev_eui)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Borne {channels.length} point{channels.length > 1 ? "s" : ""} · Supervision en direct
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Tooltip title="Réglages de l'établissement : code d'accès, tarif, anti-ventouse">
            <Button
              size="small" variant={settingsOpen ? "contained" : "outlined"}
              startIcon={<Tune />}
              onClick={() => setSettingsOpen((o) => !o)}
              sx={{ mr: 0.5, textTransform: "none", fontWeight: 600 }}
            >
              Réglages
            </Button>
          </Tooltip>
          {isAdmin && (
            <Tooltip title="Redémarrer la carte de commande (admin) — refusé si une charge est en cours">
              <span>
                <Button
                  size="small" variant="outlined" color="warning"
                  startIcon={resetBusy ? <CircularProgress size={14} color="inherit" /> : <RestartAlt />}
                  disabled={resetBusy}
                  onClick={async () => {
                    if (!window.confirm(
                      "Redémarrer la carte de commande de cette borne ?\n\n" +
                      "À n'utiliser que si la borne répond mal (aucune charge ne doit être en cours — " +
                      "le serveur et la borne le vérifient). Les points seront refermés automatiquement.")) return;
                    setResetBusy(true); setResetMsg(null);
                    const res = await postData(`/irve/${parent.dev_eui}/command`, { cmd: "reset", connector: 1 });
                    const status = res?.status || res?.response?.status;
                    if (!status || status >= 400) {
                      setResetMsg(res?.response?.data?.detail || "Reset refusé — serveur injoignable.");
                    } else {
                      setResetMsg("Reset envoyé — la carte redémarre (~1 min), points refermés automatiquement.");
                    }
                    setResetBusy(false);
                    setTimeout(() => setResetMsg(null), 10000);
                  }}
                  sx={{ mr: 0.5, textTransform: "none", fontWeight: 600 }}
                >
                  Redémarrer
                </Button>
              </span>
            </Tooltip>
          )}
          <OnlineChip timeUpdated={parent.time_updated} />
          <IconButton size="small" onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
          </IconButton>
        </Stack>
      </Stack>
      {resetMsg && (
        <Typography variant="caption" sx={{ px: { xs: 2, md: 2.5 }, pb: 1, display: "block" }}
          color={resetMsg.startsWith("Reset envoyé") ? "success.main" : "error"}>
          {resetMsg}
        </Typography>
      )}

      <Collapse in={settingsOpen} timeout="auto">
        <BorneSettings devEui={parent.dev_eui} open={settingsOpen} />
      </Collapse>

      <Collapse in={!collapsed} timeout="auto">
        <Box sx={{ px: { xs: 2, md: 2.5 }, pb: { xs: 2, md: 2.5 } }}>
          {/* Métriques agrégées */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Metric icon={<Bolt />} color="var(--kpi-rail-elec)" label="Énergie (période)" value={`${totals.energy.toFixed(1)} kWh`} sub="tous points" />
            <Metric icon={<Power />} color={C_CHARGE.main} label="Puissance instantanée" value={`${totals.power.toFixed(1)} kW`} />
            <Metric icon={<EvStation />} color={C_CHARGE.dark} label="Occupation" value={`${totals.occupied}/${totals.total}`} sub="Points occupés" />
            <Metric icon={<EuroSymbol />} color={C_FREE.dark} label="Facturé aujourd'hui"
              value={billing == null ? "…" : fmtCents(finToday ? finToday.gross_cents : 0)}
              sub={billing == null ? "" : `net ≈ ${fmtCents(netCents(finToday?.gross_cents, finToday?.tx))}`}
              onClick={() => setFinOpen((o) => !o)}
              title="Voir le détail jour par jour (14 jours)" />
          </Grid>

          {/* Détail financier jour par jour (clic sur le KPI « Facturé ») */}
          <Collapse in={finOpen} timeout="auto" unmountOnExit>
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: "1px solid", borderColor: CARD_BORDER, bgcolor: CARD_BG }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Facturé via l'application — 14 derniers jours
              </Typography>
              {!billing || billing.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aucune session facturée sur les 14 derniers jours.
                </Typography>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85em" }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ textAlign: "left", color: "text.secondary" }}>
                        {["Jour", "Transactions", "Brut", "Net estimé"].map((h) => (
                          <Box component="th" key={h} sx={{ p: 0.75, borderBottom: "2px solid", borderColor: "divider" }}>{h}</Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {billing.map((d) => (
                        <Box component="tr" key={d.date}>
                          <Box component="td" sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider", whiteSpace: "nowrap" }}>
                            {new Date(d.date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit" })}
                            {d.date === todayIso ? " (auj.)" : ""}
                          </Box>
                          <Box component="td" sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider" }}>{d.tx}</Box>
                          <Box component="td" sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider", fontWeight: 600 }}>{fmtCents(d.gross_cents)}</Box>
                          <Box component="td" sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider", color: "text.secondary" }}>{fmtCents(netCents(d.gross_cents, d.tx))}</Box>
                        </Box>
                      ))}
                      <Box component="tr">
                        <Box component="td" sx={{ p: 0.75, fontWeight: 700 }}>Total 14 j</Box>
                        <Box component="td" sx={{ p: 0.75, fontWeight: 700 }}>{finTxTotal}</Box>
                        <Box component="td" sx={{ p: 0.75, fontWeight: 700 }}>{fmtCents(finTotal)}</Box>
                        <Box component="td" sx={{ p: 0.75, fontWeight: 700 }}>{fmtCents(netCents(finTotal, finTxTotal))}</Box>
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                    Brut = montants capturés par l'application (sessions QR). Net estimé = brut −
                    frais bancaires (Stripe, cartes UE : 1,5 % + 0,25 € par transaction) ; les
                    frais réels exacts figurent dans votre interface Stripe. Les charges par badge
                    ou dashboard ne passent pas par l'application et n'apparaissent pas ici.
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>

          {/* Tuiles par point de charge */}
          <Grid container spacing={2.5}>
            {channels.map((ch) => {
              const conn = channelNumber(ch.dev_eui);
              return (
                <ChargePointCard
                  key={ch.dev_eui}
                  parentEui={parent.dev_eui}
                  connector={conn}
                  live={liveState[conn] || {}}
                  selected={selected === conn}
                  onSelect={() => setSelected(selected === conn ? null : conn)}
                  onCommanded={pollState}
                />
              );
            })}
          </Grid>

          {/* Panneau de détail (un seul, sous la grille) */}
          <Collapse in={!!selChannel} unmountOnExit timeout="auto">
            {selChannel && (
              <Box sx={{ position: "relative", mt: 5 }}>
                <Box sx={{
                  position: "absolute", top: -9, left: `calc(${caretPct}% - 9px)`,
                  width: 0, height: 0,
                  borderLeft: "9px solid transparent",
                  borderRight: "9px solid transparent",
                  borderBottom: `9px solid ${CARD_BG}`,
                  display: { xs: "none", md: "block" }, zIndex: 1,
                }} />
                <DetailPanel
                  parentEui={parent.dev_eui}
                  connector={selected}
                  channelEui={selChannel.dev_eui}
                  channelName={selChannel.name}
                  live={liveState[selected] || {}}
                  history={findHistory(deviceHistories, selChannel.dev_eui)}
                  groupedBy={groupedBy}
                  onClose={() => setSelected(null)}
                />
              </Box>
            )}
          </Collapse>
        </Box>
      </Collapse>
    </Card>
  );
}

// Pastille de vie de la borne, basée sur son dernier uplink réel (time_updated).
// La borne n'émet que sur événement : vert = trame < ONLINE_STALE_H, ambre =
// silence prolongé (à vérifier), gris = jamais vue. La vraie détection hors
// ligne (LWT MQTT) viendra avec le canal montant v2.
function OnlineChip({ timeUpdated }) {
  const age = uplinkAgeHours(timeUpdated);
  let color = "#9e9e9e", label = "État inconnu", tip = "Aucune trame reçue de la borne.";
  if (age != null) {
    tip = `Dernière trame reçue : ${fmtDateTime(timeUpdated)}`;
    if (age <= ONLINE_STALE_H) {
      color = C_FREE.main;
      label = "En service";
    } else {
      color = C_WAIT.main;
      label = `Silencieuse depuis ${fmtAge(age)}`;
    }
  }
  return (
    <Tooltip title={tip} arrow>
      <Chip size="small" icon={<FiberManualRecord sx={{ fontSize: "0.7rem !important", color: `${color} !important` }} />}
        label={label}
        sx={{ bgcolor: `${color}1f`, color, fontWeight: 600, border: `1px solid ${color}55` }} />
    </Tooltip>
  );
}

function Metric({ icon, color, label, value, sub, onClick, title }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Box onClick={onClick} title={title}
        sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.75, borderRadius: 2,
        border: "1px solid", borderColor: CARD_BORDER, bgcolor: CARD_BG,
        ...(onClick ? { cursor: "pointer", "&:hover": { borderColor: color } } : {}) }}>
        <Avatar variant="rounded" sx={{ bgcolor: color, width: 40, height: 40 }}>{icon}</Avatar>
        <Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
            {value}{sub && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{sub}</Typography>}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
}

function ChargePointCard({ parentEui, connector, live, selected, onSelect, onCommanded }) {
  const [busy, setBusy] = useState(false);
  const [cmdError, setCmdError] = useState(null);
  const charging = !!live.charging;
  const occupied = !!live.occupied || charging;
  // Pilotage par disponibilité : les points sont FERMÉS par défaut (état 8) et
  // ne passent à l'état 0 (« Available ») que sur ouverture 0x57 — dashboard,
  // badge OU activation QR payée. Un état 0 sans charge = point ATTRIBUÉ à
  // quelqu'un, jamais « libre » (l'afficher libre a caché les activations de
  // l'appli Plugy, banc 2026-08-21).
  const opened = !occupied && live.state_code === 0;
  const waiting = (occupied && !charging) || opened; // attribué, pas encore en charge
  // Armé = point ouvert mais aucune charge n'a jamais démarré (pas de
  // started_at). Seule action utile : « Libérer » (le firmware ignore les stops
  // sans charge — garde anti-stop-latent). L'expiration backend le libère de
  // toute façon au bout de ~16 min. ⚠️ « Libérer » un point armé coupe aussi
  // une activation QR en cours : l'appli du client détecte la fermeture et
  // rembourse — sans perte d'argent, mais à réserver aux points coincés.
  const armed = waiting && !live.started_at;
  const fault = !!live.fault || live.error_code > 0;
  const theme = fault ? C_FAULT : charging ? C_CHARGE : waiting ? C_WAIT : C_FREE;
  const statusLabel = fault ? "DÉFAUT" : charging ? "EN CHARGE"
    : waiting ? "SESSION EN COURS" : "LIBRE";

  const sendCommand = async (cmd, e) => {
    e.stopPropagation();
    setBusy(true);
    setCmdError(null);
    try {
      // postData n'émet jamais d'exception : en échec il RETOURNE l'erreur axios.
      const res = await postData(`/irve/${parentEui}/command`, { cmd, connector });
      const status = res?.status || res?.response?.status;
      if (!status || status >= 400) {
        const detail = res?.response?.data?.detail;
        setCmdError(detail || "Commande refusée — borne ou serveur injoignable.");
        setTimeout(() => setCmdError(null), 6000);
      } else {
        setTimeout(() => onCommanded && onCommanded(), 1500);
      }
    } finally {
      setBusy(false);
    }
  };

  const elapsed = fmtElapsed(live.started_at);

  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card variant="outlined"
        onClick={onSelect}
        title={selected ? "Masquer le détail" : "Voir le détail du point"}
        sx={{
          p: 1.75, borderRadius: 2.5, height: "100%", display: "flex", flexDirection: "column",
          border: "1px solid", borderColor: selected ? theme.main : CARD_BORDER,
          bgcolor: `${TILE_BG} !important`,
          boxShadow: selected ? `0 0 0 1px ${theme.main}` : "none",
          transition: "box-shadow .15s, border-color .15s",
          cursor: "pointer",
          "&:hover": { borderColor: theme.main },
        }}
      >
        {/* En-tête tuile */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Point {connector}</Typography>
          <Stack direction="row" alignItems="center" gap={0.5}
            sx={{ px: 1, py: 0.3, borderRadius: 5, bgcolor: `${theme.main}22`, border: `1px solid ${theme.main}55` }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: theme.main,
              ...(charging ? { animation: `${pulse} 1.4s ease-in-out infinite` } : {}) }} />
            <Typography variant="caption" sx={{ color: theme.main, fontWeight: 700, fontSize: "0.68rem", letterSpacing: 0.3 }}>
              {statusLabel}
            </Typography>
          </Stack>
        </Stack>

        {/* Corps */}
        <Box sx={{ flexGrow: 1, minHeight: 92 }}>
          {fault ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", color: C_FAULT.dark }}>
              <ErrorOutline sx={{ fontSize: 34 }} />
              <Typography variant="body2" sx={{ mt: 0.5 }}>Code {live.error_code != null ? live.error_code : "?"}</Typography>
            </Stack>
          ) : charging ? (
            <Box>
              <Stack direction="row" alignItems="baseline" gap={0.5} sx={{ mb: 1.2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.main, lineHeight: 1 }}>
                  {live.energy_kwh != null ? live.energy_kwh.toFixed(2) : "—"}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600 }}>kWh</Typography>
              </Stack>
              <InfoRow icon={<Power fontSize="small" />} text={`${live.power_kw != null ? live.power_kw.toFixed(1) : "0.0"} kW`} />
              {elapsed && <InfoRow icon={<AccessTime fontSize="small" />} text={elapsed} />}
              {live.id_tag && <InfoRow icon={<Person fontSize="small" />} text={live.id_tag} />}
            </Box>
          ) : waiting ? (
            <Box>
              <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1.2, color: C_WAIT.dark }}>
                <AccessTime sx={{ fontSize: 22 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {armed ? "En attente de branchement" : "En attente de tour"}
                </Typography>
              </Stack>
              <InfoRow icon={<Bolt fontSize="small" />} text={`${live.energy_kwh != null ? live.energy_kwh.toFixed(2) : "0.00"} kWh`} />
              {elapsed && <InfoRow icon={<AccessTime fontSize="small" />} text={elapsed} />}
              {live.id_tag && <InfoRow icon={<Person fontSize="small" />} text={live.id_tag} />}
            </Box>
          ) : (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
              <EvStation sx={{ fontSize: 38, color: C_FREE.main, opacity: 0.65 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Disponible</Typography>
            </Stack>
          )}
        </Box>

        {/* Actions */}
        <Stack direction="row" gap={1} sx={{ mt: 1.5 }}>
          {fault ? (
            <Button size="small" variant="contained" color="error" fullWidth disableElevation
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Replay />}
              disabled={busy} onClick={(e) => sendCommand("stop", e)}
              sx={{ bgcolor: `${C_STOP.main} !important`, color: "#fff", "&:hover": { bgcolor: `${C_STOP.hover} !important` } }}>Reset</Button>
          ) : armed ? (
            <Button size="small" variant="contained" fullWidth disableElevation
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Replay />}
              disabled={busy} onClick={(e) => sendCommand("release", e)}
              sx={{ bgcolor: `${C_WAIT.main} !important`, color: "#fff", "&:hover": { bgcolor: `${C_WAIT.dark} !important` } }}>Libérer</Button>
          ) : occupied ? (
            <Button size="small" variant="contained" color="error" fullWidth disableElevation
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Stop />}
              disabled={busy} onClick={(e) => sendCommand("stop", e)}
              sx={{ bgcolor: `${C_STOP.main} !important`, color: "#fff", "&:hover": { bgcolor: `${C_STOP.hover} !important` } }}>Arrêter</Button>
          ) : (
            <Button size="small" variant="contained" color="success" fullWidth disableElevation
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />}
              disabled={busy} onClick={(e) => sendCommand("start", e)}
              sx={{ bgcolor: `${C_START.main} !important`, color: "#fff", "&:hover": { bgcolor: `${C_START.hover} !important` } }}>Démarrer</Button>
          )}
        </Stack>
        {cmdError && (
          <Typography variant="caption" sx={{ mt: 0.75, color: C_FAULT.main, lineHeight: 1.3 }}>
            {cmdError}
          </Typography>
        )}
      </Card>
    </Grid>
  );
}

function InfoRow({ icon, text }) {
  return (
    <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.4, color: "text.secondary" }}>
      {icon}
      <Typography variant="body2" noWrap>{text}</Typography>
    </Stack>
  );
}

/* Graphe d'UNE session passée (demande Yan 2026-08-26) : cliquer une ligne de
 * l'historique déplie sa courbe de puissance, reconstituée depuis Influx par
 * le backend — sans avoir à naviguer le dashboard vers le jour concerné. */
function SessionCurve({ parentEui, sessionId }) {
  const [curve, setCurve] = useState(null);

  useEffect(() => {
    let alive = true;
    setCurve(null);
    (async () => {
      const res = await fetchData(`/irve/${parentEui}/sessions/${sessionId}/curve`);
      if (alive) setCurve(res && res.points ? res : { points: [] });
    })();
    return () => { alive = false; };
  }, [parentEui, sessionId]);

  if (curve == null) {
    return <Box sx={{ py: 2, textAlign: "center" }}><CircularProgress size={18} /></Box>;
  }
  if (!curve.points.length) {
    return (
      <Typography variant="caption" color="text.secondary"
        sx={{ display: "block", py: 1.5, textAlign: "center" }}>
        Pas de télémétrie disponible sur la fenêtre de cette session.
      </Typography>
    );
  }
  const data = curve.points.map((p) => ({
    t: new Date(p.t).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
    puissance: p.power_kw,
  }));
  return (
    <Box sx={{ py: 1 }}>
      <ResponsiveContainer width="100%" height={150}>
        <AreaChart data={data} margin={{ top: 5, right: 8, left: -14, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
          <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={30} />
          <YAxis tick={{ fontSize: 10 }} unit=" kW" width={58} />
          <RechartsTooltip
            formatter={(v) => [v != null ? `${v} kW` : "—", "Puissance"]}
            contentStyle={{ fontSize: "0.8em" }} />
          <Area type="monotone" dataKey="puissance" stroke="var(--kpi-rail-elec)"
            fill="var(--kpi-rail-elec)" fillOpacity={0.25} connectNulls />
        </AreaChart>
      </ResponsiveContainer>
      <Typography variant="caption" color="text.secondary"
        sx={{ display: "block", textAlign: "center" }}>
        Puissance délivrée (kW){curve.energy_wh != null
          ? ` — ${(curve.energy_wh / 1000).toFixed(2)} kWh au total` : ""}
        {curve.clock_estimated ? " · fenêtre estimée (horloge borne)" : ""}
      </Typography>
    </Box>
  );
}

function DetailPanel({ parentEui, connector, channelEui, channelName, live, history, groupedBy, onClose }) {
  const [sessions, setSessions] = useState(null);
  const [curveId, setCurveId] = useState(null);   // session dépliée (graphe)
  const [histExpanded, setHistExpanded] = useState(false); // 5 dernières par défaut

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await fetchData(`/irve/${parentEui}/sessions?connector=${connector}&limit=20`);
      if (alive) setSessions(res && res.sessions ? res.sessions : []);
    })();
    return () => { alive = false; };
  }, [parentEui, connector]);

  const eh = elapsedHours(live.started_at);
  const avgPower = live.energy_kwh != null && eh && eh > 0 ? live.energy_kwh / eh : null;

  // Série RÉELLEMENT cumulée pour le graphe : DeviceGraph ignore sa prop
  // `accumulate` (constat 2026-08-27) — la légende disait « cumulée » sur des
  // barres par intervalle, d'où un total incohérent avec le KPI Énergie.
  // On cumule donc ici (tri + dédoublonnage horodatage compris : la vue
  // journalière peut livrer des lignes dupliquées).
  const cumulHistory = useMemo(() => {
    const seen = new Set();
    const rows = (history || [])
      .filter((e) => e && e.time_created != null && isFinite(Number(e.total_used)))
      .filter((e) => (seen.has(e.time_created) ? false : (seen.add(e.time_created), true)))
      .sort((a, b) => new Date(a.time_created) - new Date(b.time_created));
    let acc = 0;
    return rows.map((e) => ({ ...e, total_used: (acc += Number(e.total_used)) }));
  }, [history]);

  const kpis = [
    { label: "Énergie délivrée", value: live.energy_kwh != null ? `${live.energy_kwh.toFixed(2)} kWh` : "—", icon: <Bolt sx={{ fontSize: 15 }} /> },
    { label: "Puissance moyenne", value: avgPower != null ? `${avgPower.toFixed(1)} kW` : "—", icon: <Power sx={{ fontSize: 15 }} /> },
    { label: "Durée de session", value: fmtElapsed(live.started_at) || "—", icon: <AccessTime sx={{ fontSize: 15 }} /> },
    { label: "Début", value: fmtDateTime(live.started_at), icon: <CalendarMonth sx={{ fontSize: 15 }} /> },
    { label: "Utilisateur", value: live.id_tag || "—", icon: <Person sx={{ fontSize: 15 }} /> },
  ];

  return (
    <Card variant="outlined" sx={{ borderRadius: 2.5, bgcolor: CARD_BG, borderColor: CARD_BORDER }}>
      {/* En-tête détail */}
      <Stack direction="row" alignItems="center" gap={1} sx={{ p: 2, pb: 1 }}>
        <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: C_CHARGE.main }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Point {connector} — détail de session</Typography>
      </Stack>

      {/* KPIs — cellules bordées (maquette) */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, px: 2, pb: 1.5 }}>
        {kpis.map((k) => (
          <Box key={k.label} sx={{ flex: "1 1 140px", minWidth: 120, p: 1.25,
            borderRadius: 1.5, border: "1px solid", borderColor: CARD_BORDER, bgcolor: "rgba(255,255,255,0.03)" }}>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.25 }}>
              <Box sx={{ color: C_CHARGE.main, display: "flex" }}>{k.icon}</Box>
              <Typography variant="caption" color="text.secondary">{k.label}</Typography>
            </Stack>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{k.value}</Typography>
          </Box>
        ))}
      </Box>

      <Divider />

      {/* Courbe + historique côte à côte */}
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Énergie du point (période affichée)</Typography>
          <Box sx={{ borderRadius: 2, p: 0 }}>
            <DeviceGraph
              data={cumulHistory}
              value="total_used"
              unit="kWh"
              typeName="electric"
              chartType="bar"
              groupedBy={groupedBy}
              aspect={3}
              colorOverride="var(--kpi-rail-elec)"
              bare={true}
            />
            <Stack direction="row" alignItems="center" justifyContent="center" gap={0.75} sx={{ mt: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: "var(--kpi-rail-elec)" }} />
              <Typography variant="caption" color="text.secondary">
                Énergie cumulée (kWh)
                {cumulHistory.length
                  ? ` — ${cumulHistory[cumulHistory.length - 1].total_used.toFixed(1)} kWh sur la période`
                  : ""}
              </Typography>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Historique des sessions</Typography>
          {sessions == null ? (
            <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={20} /></Box>
          ) : sessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Aucune session enregistrée.</Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85em" }}>
                <Box component="thead">
                  <Box component="tr" sx={{ textAlign: "left", color: "text.secondary" }}>
                    {["Début", "Durée", "Énergie", "Facturé", "Origine", "Fin"].map((h) => (
                      <Box component="th" key={h} sx={{ p: 0.75, borderBottom: "2px solid", borderColor: "divider" }}>{h}</Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {(histExpanded ? sessions : sessions.slice(0, 5)).map((s) => {
                    const failed = isFailedStart(s);
                    // Tant que la borne n'horodate pas ses sessions (start_ts=0,
                    // correctif firmware à venir), on affiche en secours l'heure
                    // de RÉCEPTION de la trame de fin, marquée « ≈ ».
                    const debut = s.started_at
                      ? fmtDateTime(s.started_at)
                      : s.received_at ? `≈ ${fmtDateTime(s.received_at)}` : "—";
                    const fin = failed
                      ? "Échec démarrage"
                      : s.stop_reason != null
                        ? (STOP_REASON_LABELS[s.stop_reason] || `code ${s.stop_reason}`)
                        : "—";
                    return (
                      <React.Fragment key={s.id}>
                        <Box component="tr"
                          onClick={() => setCurveId(curveId === s.id ? null : s.id)}
                          title="Afficher la courbe de la session"
                          sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" },
                                ...(failed ? { opacity: 0.55 } : {}),
                                ...(curveId === s.id ? { bgcolor: "action.selected" } : {}) }}>
                          <Box component="td" sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider", whiteSpace: "nowrap" }}>{debut}</Box>
                          <Box component="td" sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider" }}>{fmtDuration(s.duration_s)}</Box>
                          <Box component="td" sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider", fontWeight: 600 }}>{s.energy_wh != null ? (s.energy_wh / 1000).toFixed(2) + " kWh" : "—"}</Box>
                          <Box component="td" sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider", fontWeight: 600, whiteSpace: "nowrap" }}>
                            {s.billed_cents == null ? "—"
                              : s.billed_cents === 0 ? "Offert"
                              : (s.billed_cents / 100).toFixed(2).replace(".", ",") + " €"}
                          </Box>
                          <Box component="td" sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider" }}>{s.billing_ref ? "Application" : (ORIGIN_LABELS[s.origin] || s.origin || "—")}</Box>
                          <Box component="td" sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider", ...(failed ? { color: C_WAIT.dark, fontWeight: 600 } : {}) }}>{fin}</Box>
                        </Box>
                        {curveId === s.id && (
                          <Box component="tr">
                            <Box component="td" colSpan={6}
                              sx={{ p: 0.5, borderBottom: "1px solid", borderColor: "divider" }}>
                              <SessionCurve parentEui={parentEui} sessionId={s.id} />
                            </Box>
                          </Box>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box>
              {sessions.length > 5 && (
                <Box sx={{ textAlign: "center", mt: 0.5 }}>
                  <Button size="small" color="inherit"
                    startIcon={histExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    onClick={() => setHistExpanded((e) => !e)}
                    sx={{ color: "text.secondary", textTransform: "none" }}>
                    {histExpanded
                      ? "Réduire l'historique"
                      : `Afficher les ${sessions.length - 5} précédentes`}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>

      <Divider />
      <Box sx={{ textAlign: "center", py: 1 }}>
        <Button size="small" color="inherit" startIcon={<KeyboardArrowUp />} onClick={onClose}
          sx={{ color: "text.secondary" }}>Réduire le détail</Button>
      </Box>
    </Card>
  );
}
